/**
 * Undo and redo for whole documents (docs/spec/07 §4). Documents are immutable, so a step is just
 * the document as it was. Edits that carry the same tag one after another (typing a name, holding
 * a stepper) merge into one step. Pure.
 */

export const UNDO_LIMIT = 100;

export class History<T> {
  private past: T[] = [];
  private future: T[] = [];
  private lastTag: string | null = null;

  constructor(private readonly limit = UNDO_LIMIT) {}

  /** Remember `before`, the document an edit is about to replace. */
  record(before: T, tag?: string): void {
    this.future = [];
    if (tag !== undefined && tag === this.lastTag) return;
    this.past.push(before);
    if (this.past.length > this.limit) this.past.shift();
    this.lastTag = tag ?? null;
  }

  /** The document before the last step, or null; `current` becomes redoable. */
  undo(current: T): T | null {
    const prev = this.past.pop();
    if (prev === undefined) return null;
    this.future.push(current);
    this.lastTag = null;
    return prev;
  }

  redo(current: T): T | null {
    const next = this.future.pop();
    if (next === undefined) return null;
    this.past.push(current);
    this.lastTag = null;
    return next;
  }

  /** The next tagged edit starts a step of its own. */
  seal(): void {
    this.lastTag = null;
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.lastTag = null;
  }

  get undoCount(): number {
    return this.past.length;
  }

  get redoCount(): number {
    return this.future.length;
  }
}
